import React, { useMemo } from 'react';

import { useAppSelector } from 'store/hooks';
import {
  convertHomeRoute,
  convertRoute,
  convertStaticACRoute,
  convertStaticRoute,
  getActiveLanguages,
} from 'utils/language';

const AcDashboard = React.lazy(() => import('pages/AcDashboard'));
const ACBusinessProfile = React.lazy(() => import('pages/AnalyticsCenter/BusinessProfile'));
const AutomaticAssessment = React.lazy(() => import('pages/AnalyticsCenter/AutomaticAssessment'));
const AcAnalysisReport = React.lazy(() => import('pages/AnalyticsCenter/AnalysisReport'));
const BusinessNews = React.lazy(() => import('pages/AnalyticsCenter/BusinessNews'));
const BenefitHistories = React.lazy(() => import('pages/AnalyticsCenter/BenefitHistory'));
const CapitalRasingHistory = React.lazy(() => import('pages/AnalyticsCenter/CapitalRasingHistory'));
const ACEconomicInformation = React.lazy(() => import('pages/AnalyticsCenter/EconomicInformation'));
const TransactionHistory = React.lazy(() => import('pages/AnalyticsCenter/TransactionHistory'));
const TechnicalAnalytic = React.lazy(() => import('pages/AnalyticsCenter/TechnicalAnalytic'));
const StockInformation = React.lazy(() => import('pages/AnalyticsCenter/StockInformation'));
const InformationAnnouncedDetail = React.lazy(() => import('pages/News/InformationAnnouncedDetail'));
const Search = React.lazy(() => import('pages/Search'));
const AboutDepartmentNav = React.lazy(() => import('navigations/aboutDepartmentNav'));
const IcServiceCategoryNav = React.lazy(() => import('navigations/icServiceCategoryNav'));
const IcServiceNav = React.lazy(() => import('navigations/icServiceNav'));
const OcNavigate = React.lazy(() => import('navigations/ocNavigate'));
const IbNavigate = React.lazy(() => import('navigations/ibNavigate'));
const RecentDealsDetail = React.lazy(() => import('pages/InvestmentBank/RecentDealsDetail'));
const TsFeeNav = React.lazy(() => import('navigations/tsFeeNav'));
const OpenTradingAccount = React.lazy(() => import('pages/TransactionSupport/OpenTradingAccount'));
const NewsTransBasisDetail = React.lazy(() => import('pages/NewsTransBasisDetail'));
const NewsDisclosureCateNav = React.lazy(() => import('navigations/newsDisclosureCateNav'));
const NewsSgdckCateNav = React.lazy(() => import('navigations/newsSgdckCateNav'));
const CareerOpportunitiesDetail = React.lazy(() => import('pages/CareerOpportunitiesDetail'));
const RmJoinReasonDetail = React.lazy(() => import('pages/RmJoinReasonDetail'));
const RmCvOnlineApplyFlow = React.lazy(() => import('pages/RmCvOnlineApplyFlow'));
const RmCvOnlineEditFlow = React.lazy(() => import('pages/RmCvOnlineEditFlow'));
const RmSavedJob = React.lazy(() => import('pages/RmSavedJob'));
const RecruitmentManageProfile = React.lazy(() => import('pages/RecruitmentManageProfile'));
const RmAppliedJob = React.lazy(() => import('pages/RmAppliedJob'));
const CVUpload = React.lazy(() => import('pages/CvUpload'));

const RegisterBondTrading = React.lazy(() => import('pages/IndividualCustomers/RegisterBondTrading'));
const BondIndividualsInfo = React.lazy(() => import('pages/IndividualCustomers/BondAndIndividualsInformation'));
const RegisterTransaction = React.lazy(() => import('pages/IndividualCustomers/RegisterTransaction'));
const CompleteRegisterBondTrading = React.lazy(() => import('pages/IndividualCustomers/CompleteRegisterBondTrading'));

const useInitializeRoutes = () => {
  const dataSystem = useAppSelector((state) => state.system.dataSystem);

  const activeLangList = useMemo(() => getActiveLanguages(dataSystem?.locales),
    [dataSystem?.locales]);

  const routesList = useMemo(
    () => {
      if (dataSystem?.locales) {
        return {
          home: convertHomeRoute(activeLangList),
          pages: convertRoute(activeLangList, '/:slug'),
          login: convertStaticRoute('LOGIN', activeLangList, true),
          changePassword: convertStaticRoute('CHANGE_PASSWORD', activeLangList, true),
          forgotPassword: convertStaticRoute('FORGOT_PASSWORD', activeLangList, true),
          recruitVerifyOtp: convertStaticRoute('RECRUIT_VERIFY_OTP', activeLangList, true),
          identityVerification: convertStaticRoute('IDENTITY_VERIFICATION', activeLangList, true),
          ekyc: convertStaticRoute('EKYC', activeLangList),
          analysisReportPreview: convertStaticRoute('ANALYSIS_REPORT_PREVIEW', activeLangList),
        };
      }
      return undefined;
    },
    [dataSystem, activeLangList],
  );

  const mainLayoutRouteList = useMemo(
    () => {
      if (dataSystem?.locales) {
        return {
          search: {
            route: convertStaticRoute('SEARCH', activeLangList, true),
            component: <Search />,
          },
          aboutDepartment: {
            route: convertStaticRoute('ABOUT_DEPARTMENT', activeLangList),
            component: <AboutDepartmentNav />,
          },
          individualCustomer: {
            route: convertStaticRoute('INDIVIDUAL_CUSTOMER', activeLangList),
            component: <IcServiceCategoryNav />,
          },
          individualCustomerDetail: {
            route: convertStaticRoute('INDIVIDUAL_CUSTOMER_DETAIL', activeLangList),
            component: <IcServiceNav />,
          },
          organizeCustomer: {
            route: convertStaticRoute('ORGANIZE_CUSTOMER', activeLangList),
            component: <OcNavigate />,
          },
          bankInvestment: {
            route: convertStaticRoute('BANK_INVESTMENT', activeLangList),
            component: <IbNavigate />,
          },
          bankInvestmentDetail: {
            route: convertStaticRoute('BANK_INVESTMENT_DETAIL', activeLangList),
            component: <RecentDealsDetail />,
          },
          transactionFee: {
            route: convertStaticRoute('TRANSACTION_FEE', activeLangList),
            component: <TsFeeNav />,
          },
          transactionSupportDetail: {
            route: convertStaticRoute('TRANSACTION_SUPPORT_DETAIL', activeLangList),
            component: <OpenTradingAccount />,
          },
          newsVcbsDetail: {
            route: [
              ...convertStaticRoute('NEWS_VCBS_DETAIL', activeLangList),
              ...convertStaticRoute('NEWS_DISCLOSURE_DETAIL', activeLangList),
            ],
            component: <InformationAnnouncedDetail />,
          },
          newsSgdckDetail: {
            route: convertStaticRoute('NEWS_SGDCK_DETAIL', activeLangList),
            component: <NewsTransBasisDetail />,
          },
          newsDisclosure: {
            route: convertStaticRoute('NEWS_DISCLOSURE', activeLangList),
            component: <NewsDisclosureCateNav />,
          },
          newsSgdck: {
            route: convertStaticRoute('NEWS_SGDCK', activeLangList),
            component: <NewsSgdckCateNav />,
          },
          registerBondTrading: {
            route: convertStaticRoute('REGISTER_BOND_TRADING', activeLangList, true),
            component: <RegisterBondTrading />,
          },
          bondIndividualsInfo: {
            route: convertStaticRoute('BOND_INDIVIDUALS_INFO', activeLangList, true),
            component: <BondIndividualsInfo />,
          },
          registerTransaction: {
            route: convertStaticRoute('TRANSACTION_REGISTER', activeLangList, true),
            component: <RegisterTransaction />,
          },
          completeRegisterBondTrading: {
            route: convertStaticRoute('COMPLETE_REGISTER_BOND', activeLangList, true),
            component: <CompleteRegisterBondTrading />,
          },
        };
      }
      return undefined;
    },
    [dataSystem, activeLangList],
  );

  const recruitmentDetailsList = useMemo(
    () => {
      if (dataSystem?.locales) {
        return {
          careerOpportunitiesDetail: {
            route: convertStaticRoute('RECRUITMENT_DETAILS', activeLangList, false),
            component: <CareerOpportunitiesDetail />,
            isAuthen: false,
          },
          joinReasonDetail: {
            route: convertStaticRoute('JOIN_REASON_DETAIL', activeLangList, false),
            component: <RmJoinReasonDetail />,
            isAuthen: false,
          },
          recruitmentMemberCvApplication: {
            route: convertStaticRoute('RECRUITMENT_CV_APPLICATION', activeLangList, true),
            component: <RmCvOnlineApplyFlow />,
            isAuthen: true,
          },
          recruitmentMemberCvDownload: {
            route: convertStaticRoute('RECRUITMENT_CV_EDIT', activeLangList, true),
            component: <RmCvOnlineEditFlow />,
            isAuthen: true,
          },
          savedJob: {
            route: convertStaticRoute('SAVED_JOB', activeLangList, true),
            component: <RmSavedJob />,
            isAuthen: true,
          },
          recruitmentRmAppliedJob: {
            route: convertStaticRoute('RM_APPLIED_JOB', activeLangList, true),
            component: <RmAppliedJob />,
            isAuthen: true,
          },
          recruitmentManageProfile: {
            route: convertStaticRoute('RECRUIT_MANAGE_PROFILE', activeLangList, true),
            component: <RecruitmentManageProfile />,
            isAuthen: true,
          },
          recruitmentMemberCvFileApplication: {
            route: convertStaticRoute('APPLIED_JOB_CV', activeLangList, true),
            component: <CVUpload />,
            isAuthen: true,
          },
        };
      }
      return undefined;
    },
    [dataSystem, activeLangList],
  );

  const acRoutes = useMemo(
    () => {
      if (dataSystem?.locales) {
        return [
          {
            route: convertStaticACRoute('DASHBOARD', activeLangList, true),
            component: AcDashboard,
          },
          {
            route: convertStaticACRoute('BUSINESS_PROFILE', activeLangList, true),
            component: ACBusinessProfile,
          },
          {
            route: convertStaticACRoute('BENEFIT_HISTORY', activeLangList, true),
            component: BenefitHistories,
          },
          {
            route: convertStaticACRoute('ANALYSIS_REPORT', activeLangList, true),
            component: AcAnalysisReport,
          },
          {
            route: convertStaticACRoute('RASING_HISTORY', activeLangList, true),
            component: CapitalRasingHistory,
          },
          {
            route: convertStaticACRoute('TRANSACTION_HISTORY', activeLangList, true),
            component: TransactionHistory,
          },
          {
            route: convertStaticACRoute('AUTOMATIC_ASSESSMENT', activeLangList, true),
            component: AutomaticAssessment,
          },
          {
            route: convertStaticACRoute('STOCK_INFORMATION', activeLangList, true),
            component: StockInformation,
          },
          {
            route: convertStaticACRoute('ECONOMIC_INFORMATION', activeLangList, true),
            component: ACEconomicInformation,
          },
          {
            route: convertStaticACRoute('TECHNICAL_ANALYTIC', activeLangList, true),
            component: TechnicalAnalytic,
          },
          {
            route: convertStaticACRoute('BUSINESS_NEWS', activeLangList, true),
            component: BusinessNews,
          },
        ];
      }
      return undefined;
    },
    [dataSystem, activeLangList],
  );

  return {
    activeLangList,
    routesList,
    acRoutes,
    mainLayoutRouteList,
    recruitmentDetailsList,
  };
};

export default useInitializeRoutes;
